import type * as React from 'react';
import { DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/**
 * A dialog that is never taller than the window.
 *
 * `DialogContent` sizes to what is in it and has no limit, so a dialog whose
 * content can grow — a list, a set of results — ends up with its footer below
 * the bottom of the window and no way to reach it. Here the header and footer
 * stay put and {@link ScrollDialogBody} between them takes the scrolling:
 *
 *   <ScrollDialogContent>
 *     <DialogHeader>…</DialogHeader>
 *     <ScrollDialogBody>…</ScrollDialogBody>
 *     <DialogFooter>…</DialogFooter>
 *   </ScrollDialogContent>
 */
export function ScrollDialogContent({
  className,
  ...props
}: React.ComponentProps<typeof DialogContent>) {
  return (
    <DialogContent
      className={cn('flex max-h-[calc(100dvh-2rem)] flex-col', className)}
      {...props}
    />
  );
}

/**
 * The part of a {@link ScrollDialogContent} that scrolls when the window is too
 * short for it. A flex column, so a child that can give up height (`min-h-*`,
 * with its own overflow) shrinks first and this scrolls only once that child
 * has nothing left to give.
 *
 * `-mx-1 px-1`: a scroll container clips sideways too, which would shave the
 * focus ring off any input at its edge.
 */
export function ScrollDialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('-mx-1 flex min-h-0 flex-col gap-4 overflow-y-auto px-1', className)}
      {...props}
    />
  );
}
